/**
 * Token Meter Component
 * Displays token usage with visual progress bar
 */
import React from 'react';
import PropTypes from 'prop-types';
import logger from '../utils/logger';

const TokenMeter = ({ tokensUsed, yearlyTokens, showLabel = true, size = 'md' }) => {
  const percentage = yearlyTokens > 0 ? Math.min((tokensUsed / yearlyTokens) * 100, 100) : 0;
  const remaining = Math.max(yearlyTokens - tokensUsed, 0);
  
  const sizeClasses = {
    sm: 'h-2',
    md: 'h-4',
    lg: 'h-6',
  };
  
  const getColorClass = () => {
    if (percentage >= 90) return 'bg-red-500';
    if (percentage >= 75) return 'bg-yellow-500';
    return 'bg-green-500';
  };
  
  logger.debug('TokenMeter rendered', { tokensUsed, yearlyTokens, percentage });
  
  const getGradientClass = () => {
    if (percentage >= 90) return 'bg-gradient-to-r from-red-500 to-rose-600';
    if (percentage >= 75) return 'bg-gradient-to-r from-yellow-400 to-amber-500';
    return 'bg-gradient-to-r from-green-500 to-emerald-500';
  };

  return (
    <div className="w-full">
      {showLabel && (
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center space-x-4">
            <div className="text-center">
              <div className="text-3xl font-bold text-primary-500">
                {tokensUsed}
              </div>
              <div className="text-xs text-gray-500 font-medium">Used</div>
            </div>
            <div className="text-gray-300 text-2xl">/</div>
            <div className="text-center">
              <div className="text-3xl font-bold text-gray-700">
                {yearlyTokens}
              </div>
              <div className="text-xs text-gray-500 font-medium">Total</div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-primary-500">
              {remaining}
            </div>
            <div className="text-xs text-gray-500 font-medium">Remaining</div>
          </div>
        </div>
      )}
      <div className={`w-full bg-gray-200 rounded-full overflow-hidden shadow-inner ${sizeClasses[size]}`}>
        <div
          className={`${getGradientClass()} transition-all duration-500 ease-out ${sizeClasses[size]} shadow-md`}
          style={{ width: `${percentage}%` }}
          role="progressbar"
          aria-valuenow={percentage}
          aria-valuemin="0"
          aria-valuemax="100"
        >
          <span className="sr-only">{percentage}% used</span>
        </div>
      </div>
      {showLabel && (
        <div className="mt-3 text-center">
          <span className="inline-block px-4 py-1.5 bg-primary-50 text-primary-700 rounded-full text-sm font-semibold">
            {percentage.toFixed(1)}% of yearly allocation used
          </span>
        </div>
      )}
    </div>
  );
};

TokenMeter.propTypes = {
  tokensUsed: PropTypes.number.isRequired,
  yearlyTokens: PropTypes.number.isRequired,
  showLabel: PropTypes.bool,
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
};

export default TokenMeter;

